import { isEmpty, isNil } from 'lodash';
import * as Yup from 'yup';
import {
  buildPolicyMessage,
  getFailedPasswordRules,
  isPasswordPolicyActive,
  PasswordPolicy,
} from '@common/utils/passwordPolicy';

export type IField = {
  default: boolean;
  fieldId: string;
  fid: string;
  label: string;
  multiline: boolean;
  name: string;
  placeholder: string;
  required: boolean;
  requiredText: string;
  type: string;
  length: number;
  typeValidateLen: string;
  validateLenText: string;
};

export const generateSchema = (
  fields: Array<IField>,
  locale: String,
  passwordPolicy?: PasswordPolicy | null
) => {
  let schema: any = {};

  fields.forEach((field) => {
    // A password field must always be validated when the policy is active,
    // even if it is not flagged required.
    const isPasswordWithPolicy =
      field.type === 'password' && isPasswordPolicyActive(passwordPolicy);

    if (!field.required && !isPasswordWithPolicy) {
      if (!isNil(field.typeValidateLen) && field.typeValidateLen !== 'none') {
        schema[field.fieldId || field.fid] = createSchema(
          field,
          locale,
          passwordPolicy
        );
      }
      return;
    }
    schema[field.fieldId || field.fid] = createSchema(
      field,
      locale,
      passwordPolicy
    );
  });
  return Yup.object().shape(schema);
};

const createSchema = (
  field: IField,
  locale: String,
  passwordPolicy?: PasswordPolicy | null
) => {
  const { type, requiredText, fieldId } = field;

  const IEmailField = fieldId === 'email';

  switch (type) {
    case 'boolean':
      // return Yup.bool().nullable(true).default(false);
      return field.required
        ? Yup.string()
            .nullable(true)
            .test(
              `${field.fieldId || field.fid}`,
              requiredText,
              (value: string | any) => value === 'true'
            )
            .required(requiredText)
        : true;

    case 'image':
      return Yup.object().nullable(true).required(requiredText);

    case 'file':
      return Yup.object().nullable(true).required(requiredText);

    case 'string':
      const { typeValidateLen, validateLenText, length } = field;
      const validLength = isNil(length) ? 0 : length;

      switch (typeValidateLen) {
        case 'min':
          return Yup.string()
            .ensure()
            .test(
              `${field.fieldId || field.fid}`,
              requiredText,
              (value: string) => {
                if (field.required) return !isEmpty(value);
                return true;
              }
            )
            .min(
              validLength,
              validateLenText?.trim()?.length
                ? validateLenText
                : `Min length is ${validLength} character`
            )
            .test(
              `${field.fieldId || field.fid}`,
              `${
                locale === 'ja'
                  ? 'メールアドレスが有効ではありません'
                  : 'This field must be a valid email'
              }`,
              (value: string) => {
                if (IEmailField) return Yup.string().email().isValidSync(value);
                return Yup.string().nullable(true).isValidSync(value);
              }
            );
        case 'max':
          return Yup.string()
            .ensure()
            .test(
              `${field.fieldId || field.fid}`,
              requiredText,
              (value: string) => {
                if (field.required) return !isEmpty(value);
                return true;
              }
            )
            .max(
              validLength,
              validateLenText?.trim()?.length
                ? validateLenText
                : `Max length is ${validLength} character`
            )
            .test(
              `${field.fieldId || field.fid}`,
              `${
                locale === 'ja'
                  ? 'メールアドレスが有効ではありません'
                  : 'This field must be a valid email'
              }`,
              (value: string) => {
                if (IEmailField) return Yup.string().email().isValidSync(value);
                return Yup.string().nullable(true).isValidSync(value);
              }
            );
        case 'equal':
          return Yup.string()
            .ensure()
            .test(
              `${field.fieldId || field.fid}`,
              requiredText,
              (value: string) => {
                if (field.required) return !isEmpty(value);
                return true;
              }
            )
            .length(
              validLength,
              validateLenText?.trim()?.length
                ? validateLenText
                : `Field must have ${validLength} character`
            )
            .test(
              `${field.fieldId || field.fid}`,
              `${
                locale === 'ja'
                  ? 'メールアドレスが有効ではありません'
                  : 'This field must be a valid email'
              }`,
              (value: string) => {
                if (IEmailField) return Yup.string().email().isValidSync(value);
                return Yup.string().nullable(true).isValidSync(value);
              }
            );
        default:
          return Yup.string()
            .ensure()
            .test(
              `${field.fieldId || field.fid}`,
              requiredText,
              (value: string) => {
                if (field.required) return !isEmpty(value);
                return true;
              }
            )
            .test(
              `${field.fieldId || field.fid}`,
              `${
                locale === 'ja'
                  ? 'メールアドレスが有効ではありません'
                  : 'This field must be a valid email'
              }`,
              (value: string) => {
                if (IEmailField) return Yup.string().email().isValidSync(value);
                return Yup.string().nullable(true).isValidSync(value);
              }
            );
      }

    case 'number': {
      const { typeValidateLen, validateLenText, length } = field;

      const validLength = isNil(length) ? 0 : length;

      switch (typeValidateLen) {
        case 'min':
          return Yup.string()
            .ensure()
            .test(
              `${field.fieldId || field.fid}`,
              requiredText,
              (value: string) => {
                if (field.required) return !isEmpty(value);
                return true;
              }
            )
            .min(validLength, validateLenText);
        case 'max':
          return Yup.string()
            .ensure()
            .test(
              `${field.fieldId || field.fid}`,
              requiredText,
              (value: string) => {
                if (field.required) return !isEmpty(value);
                return true;
              }
            )
            .max(validLength, validateLenText);
        case 'equal':
          return Yup.string()
            .ensure()
            .test(
              `${field.fieldId || field.fid}`,
              requiredText,
              (value: string) => {
                if (field.required) return !isEmpty(value);
                return true;
              }
            )
            .length(validLength, validateLenText);
        default:
          return Yup.string().nullable(true).required(requiredText);
      }
    }

    case 'password': {
      return Yup.string()
        .ensure()
        .test(
          `${field.fieldId || field.fid}`,
          requiredText,
          (value: string) => {
            if (field.required) return !isEmpty(value);
            return true;
          }
        )
        .test(
          `${field.fieldId || field.fid}-policy`,
          '',
          function (value: string) {
            if (!isPasswordPolicyActive(passwordPolicy)) return true;
            // Emptiness is handled by the required test above; only validate
            // the policy on a non-empty value.
            if (isEmpty(value)) return true;
            const failed = getFailedPasswordRules(value, passwordPolicy);
            if (!failed.length) return true;
            // Surface one message per failed rule.
            return this.createError({
              message: buildPolicyMessage(
                failed,
                passwordPolicy,
                locale === 'ja' ? 'ja' : 'en'
              ),
            });
          }
        );
    }

    default:
      return Yup.string().nullable(true).required(requiredText);
  }
};
